import { Link } from "react-router-dom";
import {
  Phone, Mail, MapPin, MessageCircle, ShieldCheck, Lock,
  ArrowRight, Cookie,
} from "lucide-react";
import { useCompany, fmtNipc } from "../context/CompanyContext";

/**
 * Premium footer for the marketing site.
 *
 * 4-column grid (desktop) / stacked (mobile) with:
 *   1. Brand + tagline + trust signals (SSL/HTTPS, secure forms)
 *   2. Legal & policies (Termos, Privacidade, Cookies, Pagamentos)
 *   3. Contact (phone, email, WhatsApp, hours, location)
 *   4. Company info (commercial name, NIF, registered address)
 *
 * Bottom strip = copyright. The visual language follows the Dark Luxe
 * palette (deep green + gold accents).
 */

const PHONE_2_DISPLAY = "+351 936 942 629";
const PHONE_2_TEL = "+351936942629";

export default function SiteFooter() {
  const year = new Date().getFullYear();
  const { company, logoDark, digits } = useCompany();
  const PHONE_DISPLAY = company.phone;
  const PHONE_TEL = `+${digits(company.phone)}`;
  const EMAIL = company.email;
  const WHATSAPP = `https://wa.me/${digits(company.whatsapp)}?text=` +
    encodeURIComponent(`Olá ${company.trade_name}, gostaria de saber mais sobre os vossos serviços.`);
  const seat = [company.address, company.postal_code, company.city].filter(Boolean).join(" · ") || "Ponta do Sol & Funchal · Madeira";
  return (
    <footer className="bg-[#031411] border-t border-[#1a4640] mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* 1. Brand */}
          <div data-testid="footer-brand">
            <Link to="/" className="inline-flex items-center" aria-label={`${company.trade_name} — Início`}>
              <span className="bg-[#fdfbf7] rounded-sm px-2.5 py-1 inline-flex items-center">
                <img src={logoDark}
                     alt={company.trade_name}
                     data-testid="footer-logo"
                     className="h-20 w-auto select-none pointer-events-none"
                     loading="lazy" decoding="async" />
              </span>
            </Link>
            <p className="mt-4 text-sm text-[#a0b2af] leading-relaxed max-w-xs">
              Limpeza profissional &amp; gestão de operações para alojamento local na Madeira.
              Ponta do Sol &amp; Funchal · Madeira · Portugal.
            </p>

            {/* Trust signals */}
            <div className="mt-6 space-y-2">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#a0b2af]">
                <Lock size={11} className="text-[#d4af37]" /> Site seguro · SSL/HTTPS
              </div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#a0b2af]">
                <ShieldCheck size={11} className="text-[#d4af37]" /> Formulários protegidos
              </div>
            </div>
          </div>

          {/* 2. Legal */}
          <div data-testid="footer-legal">
            <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37] mb-4">
              Legal &amp; Políticas
            </h3>
            <ul className="space-y-2.5 text-sm">
              <FooterLink to="/termos" testId="footer-termos-link">Termos &amp; Condições</FooterLink>
              <FooterLink to="/privacidade" testId="footer-privacidade-link">Política de Privacidade</FooterLink>
              <FooterLink to="/cookies" testId="footer-cookies-link">Política de Cookies</FooterLink>
              <FooterLink to="/pagamentos" testId="footer-pagamentos-link">Política de Pagamentos</FooterLink>
              <li className="pt-1">
                <button onClick={() => window.dispatchEvent(new CustomEvent("luxstay:open-cookie-banner"))}
                        data-testid="footer-manage-cookies"
                        className="inline-flex items-center gap-1.5 text-xs text-[#a0b2af] hover:text-[#d4af37] transition-colors">
                  <Cookie size={11} /> Gerir cookies
                </button>
              </li>
            </ul>
          </div>

          {/* 3. Contact */}
          <div data-testid="footer-contact">
            <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37] mb-4">
              Contacto
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`tel:${PHONE_TEL}`}
                   data-testid="footer-phone"
                   className="group inline-flex items-center gap-2 text-[#fdfbf7] hover:text-[#d4af37] transition-colors">
                  <Phone size={12} className="text-[#d4af37]" />
                  <span className="tracking-wide">{PHONE_DISPLAY}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${PHONE_2_TEL}`}
                   data-testid="footer-phone-2"
                   className="group inline-flex items-center gap-2 text-[#fdfbf7] hover:text-[#d4af37] transition-colors">
                  <Phone size={12} className="text-[#d4af37]" />
                  <span className="tracking-wide">{PHONE_2_DISPLAY}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`}
                   data-testid="footer-email"
                   className="inline-flex items-center gap-2 text-[#fdfbf7] hover:text-[#d4af37] transition-colors break-all">
                  <Mail size={12} className="text-[#d4af37] shrink-0" />
                  {EMAIL}
                </a>
              </li>
              <li>
                <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"
                   data-testid="footer-whatsapp"
                   className="inline-flex items-center gap-2 text-[#25D366] hover:text-[#3ee37a] transition-colors">
                  <MessageCircle size={12} /> WhatsApp
                </a>
              </li>
              <li className="text-[11px] text-[#a0b2af] pt-1">
                Seg–Sáb · 07:00–19:00
              </li>
              <li className="text-[11px] text-[#a0b2af] inline-flex items-start gap-1.5">
                <MapPin size={11} className="text-[#d4af37] shrink-0 mt-0.5" />
                Ponta do Sol &amp; Funchal · Madeira
              </li>
              <li className="pt-2">
                <Link to="/contacto"
                      data-testid="footer-contact-cta"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#d4af37] hover:text-[#e8c860] transition-colors">
                  Página de contacto <ArrowRight size={11} />
                </Link>
              </li>
            </ul>
          </div>

          {/* 4. Company info */}
          <div data-testid="footer-company">
            <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37] mb-4">
              Empresa
            </h3>
            <dl className="space-y-3 text-xs text-[#a0b2af] leading-relaxed">
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-[#a0b2af]/70">Denominação</dt>
                <dd className="text-[#fdfbf7] mt-0.5" data-testid="footer-legal-name">{company.legal_name}</dd>
              </div>
              {company.nipc && (
                <div>
                  <dt className="text-[10px] uppercase tracking-wider text-[#a0b2af]/70">NIPC</dt>
                  <dd className="text-[#fdfbf7] mt-0.5 tracking-wider" data-testid="footer-nipc">{fmtNipc(company.nipc)}</dd>
                </div>
              )}
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-[#a0b2af]/70">Email institucional</dt>
                <dd className="mt-0.5">
                  <a href={`mailto:${EMAIL}`} className="text-[#fdfbf7] hover:text-[#d4af37] transition-colors break-all">
                    {EMAIL}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-[#a0b2af]/70">Sede</dt>
                <dd className="text-[#fdfbf7] mt-0.5">{seat}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Newsletter / quick links bar — small placeholder for future ENH */}
      </div>

      {/* Bottom strip */}
      <div className="border-t border-[#1a4640]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-[#a0b2af]">
          <p data-testid="footer-copyright">
            © {year} {company.legal_name.replace(/\.$/, "")}. Todos os direitos reservados.
          </p>
          <p className="text-[#a0b2af]/60">
            Feito com cuidado no Funchal · Madeira
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <li>
      <Link to={to}
            data-testid={testId}
            className="text-[#a0b2af] hover:text-[#d4af37] transition-colors inline-flex items-center gap-1.5 group">
        {children}
        <ArrowRight size={11} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
      </Link>
    </li>
  );
}
